import type { MouseEvent } from 'react';
import { Icon } from '../../ui';
import { shouldInterceptClick } from './navTransition';
import { TransitionLink } from './RouteTransition';
import { useBack } from './useBack';

/**
 * BACK, TO WHERE THE USER ACTUALLY CAME FROM (audit P2-4).
 *
 * Six detail screens hardcoded a parent - the record page to its object list, the webhook
 * to the webhook list, and so on - so opening a related record from an agency and pressing
 * back landed on the CHILD OBJECT'S LIST, a screen the user had never seen. The owner's
 * own case, and it reproduced on every one of the six.
 *
 * THE RULE IS THE HISTORY STACK, NOT THE PATH, and since the shell round it is `useBack()`
 * (SH-A8, owner-48). The three answers and their Apple sources are documented there; what
 * changed here is the middle one. This component used to ask `historyIndex` alone, and a
 * document LOAD resets that index to 0 - so a person who was standing on Home in this tab,
 * opened a pane by link and pressed back was treated as a stranger and sent to the stated
 * parent, which is exactly the People list the owner landed on. The tab's own memory of
 * the last app path now answers that case before the fallback does.
 *
 * IT IS STILL A LINK, and that is deliberate. The `href` is the fallback parent, so
 * middle-click, cmd-click, "copy link address" and the status bar all keep working - none
 * of which a `<button>` can offer. Only the plain left click is taken over, exactly as
 * `TransitionLink` already takes one over, and `Link` composes our handler first and skips
 * its own navigation when it is prevented (verified in the installed react-router 8.3.0,
 * `lib/dom/lib.js:314`).
 *
 * THE POP TRANSITION SURVIVES BOTH WAYS. A real `history.back()` fires `popstate`, which
 * `RouteTransitionProvider` already animates as a pop - it is the same path the browser's
 * own back button takes. The fallback keeps `TransitionLink direction="pop"`, which is
 * what these six screens did before. Nothing here can produce a push.
 */
export interface BackLinkProps {
  /** Where back goes when this screen was opened cold: the stated parent. */
  readonly to: string;
  readonly label: string;
  /**
   * REPLACES the default styling rather than adding to it. The six screens this wave
   * touches wore four different back controls, and merging class lists would have let
   * Tailwind's emission order decide which font size wins - so a screen that already had
   * its own look keeps it exactly.
   *
   * THE CHEVRON IS NO LONGER OPTIONAL (wave 3, owner-45/46). One screen used to draw this
   * as a trailing underlined text link inside the toolbar's action capsule; a back control
   * is a leading chevron and a word, so that screen now draws what the others draw and the
   * `showIcon` escape hatch is gone with it.
   */
  readonly className?: string | undefined;
  /** What the chrome around it needs to mark it with; nothing else reaches the element. */
  readonly 'data-toolbar-item'?: string | undefined;
  /**
   * INSIDE THE WINDOW'S PAIR THE CONTROL IS A SYMBOL (wave 4 round 2, item 0b). Apple's
   * macOS back and forward pair carries a chevron and no word on every window - Finder
   * (owner-63), System Settings (owner-82), and the kit's own group master
   * `Titlebars and Toolbars/Dark/XL/Buttons/Active/2`, whose two 28px inner buttons hold a
   * symbol each and have no label slot at all (searching all 396 masters of
   * `Titlebars and Toolbars` for `label` returns the window title and the search
   * placeholder, nothing else). A1 left the record's own control in that group with
   * "Team members" written on it, so one window carried a labelled back and every other a
   * symbol-only one. The word becomes the help tag, which is where Apple puts the previous
   * screen's title on a Mac, and the accessible name stays the screen behind it.
   */
  readonly inPair?: boolean | undefined;
}

const BASE =
  'pressable -ml-1 inline-flex min-h-(--touch-target-min) items-center gap-1.5 rounded-control px-1 text-sm text-ink-muted hover:text-ink';

/**
 * THE SAME BOX THE PAIR'S OWN BACK HAS. `IconButton` at its default size is
 * `size-(--touch-target-min)`, and the group's 28px inner button is what the toolbar's own
 * rule then gives it (`index.css`, `.shell-toolbar-group > button`), so a control that
 * joins the group has to be the same square rather than a text-sized capsule.
 */
const PAIR =
  'inline-flex size-(--touch-target-min) items-center justify-center rounded-(--radius-button) text-ink';

export function BackLink({ to, label, className }: BackLinkProps) {
  const back = useBack(to);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!shouldInterceptClick(event)) return;
    event.preventDefault();
    /**
     * The link's `href` is the last resort and it stays one: if `goBack` finds nothing to
     * pop and nothing remembered, it navigates to `to` itself, so this never falls through
     * to a default that has already been prevented.
     */
    back.goBack();
  };

  return (
    <TransitionLink direction="pop" to={to} className={className ?? BASE} onClick={handleClick}>
      <Icon name="chevron-left" size="0.9rem" />
      {label}
    </TransitionLink>
  );
}

/**
 * THE SAME CONTROL AS A BUTTON, FOR A TOOLBAR (wave 3 round 2, Parent A's review check on
 * "toolbar content").
 *
 * Apple's toolbar carries buttons, menus, segmented controls and search, and the record page
 * was putting this control - an anchor - into the window toolbar's leading area, where it read
 * as a text link inside the chrome. hig:
 * https://developer.apple.com/design/human-interface-guidelines/toolbars
 *
 * THE BEHAVIOUR IS `useBack()`'s, the same call `BackLink` above makes (SH-A8): the app's own
 * push first, then the tab's memory of the last app path, then the stated parent. There is
 * ONE back implementation in the product and this is not a second one - which is why the
 * `historyIndex` test this export was born with is gone rather than kept beside the hook's.
 *
 * What differs is the ELEMENT, and with it the three things an anchor offers that a toolbar
 * item must not - cmd-click, the status bar and "copy link address" - which is why this is a
 * second export rather than a flag on the first: every other back control in the product sits
 * in page CONTENT, where a link is the right element and those three are worth having. A
 * button has no `href` to fall back to either, so the fallback is the hook's own last answer
 * and not the browser's.
 */
export function BackButton({ to, label, className, inPair = false, ...rest }: BackLinkProps) {
  const back = useBack(to);

  return (
    /* THE CHROME AROUND IT MAY MARK IT (wave 4 round 1, C2-7). The window toolbar seats this
       control as the leading half of its back and forward pair and clones the pair's own
       `data-toolbar-item` onto it, so a review reads one back control rather than a page's
       and the window's; a component that swallowed what was passed to it would have been
       marked and drawn nothing. */
    <button
      type="button"
      aria-label={label}
      /* The help tag is a verb phrase naming the screen behind it (copy deck R10), and it
         is a `title` rather than the `Tooltip` primitive because this control is a page's
         own node cloned into the chrome: a wrapper here would be cloned with it and the
         mark the toolbar puts on the element would land on the wrapper instead. */
      title={inPair ? `Go back to ${label}` : undefined}
      className={className ?? (inPair ? PAIR : BASE)}
      onClick={() => {
        back.goBack();
      }}
      {...rest}
    >
      <Icon name="chevron-left" size="0.9rem" />
      {!inPair && label}
    </button>
  );
}
